import { useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { CollapsePanel } from '@/components/ui/CollapsePanel'
import { Skeleton } from '@/components/ui/skeleton'
import { ExchangeTransactionLogChart } from '@/components/ExchangeTransactionLogChart'
import { FolderUsageErrorBoundary, FolderUsageList } from '@/components/FolderUsageList'
import {
  EXCHANGE_HEALTH_SLUG,
  EXCHANGE_TAB_DESCRIPTION,
  exchangeHealthSnapshotFromRmmData,
  groupExchangeChecks,
  transactionLogSeriesFromPayload,
} from '@/lib/exchangeHealth'
import { formatDateTime } from '@/lib/formatAgent'
import { storageBreakdownFromExchangePayload } from '@/lib/folderUsage'
import {
  assessmentChecks,
  checkStatusLabel,
  checkStatusVariant,
  normaliseCheckStatus,
  normaliseToolkitStatus,
  toolkitStatusLabel,
} from '@/lib/toolkitHealth'
import { cn } from '@/lib/utils'

function statusPillVariant(status) {
  const normalised = normaliseToolkitStatus(status)
  if (normalised === 'PASS') return 'ready'
  if (normalised === 'WARN') return 'pending'
  if (normalised === 'FAIL') return 'destructive'
  return 'inactive'
}

function CountTile({ label, value, variant, active, onClick }) {
  const clickable = Boolean(onClick) && (value ?? 0) > 0
  const Tag = clickable ? 'button' : 'div'

  return (
    <Tag
      type={clickable ? 'button' : undefined}
      onClick={clickable ? onClick : undefined}
      className={cn(
        'rounded-md border border-border px-3 py-2 text-left transition-colors',
        clickable && 'cursor-pointer hover:border-primary/60 hover:bg-muted/40',
        active && 'border-primary bg-primary/5 ring-1 ring-primary/20',
        !clickable && 'opacity-90',
      )}
    >
      <p className="text-xs text-muted-foreground">{label}</p>
      {variant ? (
        <Badge variant={variant} className="mt-1">{value ?? 0}</Badge>
      ) : (
        <p className="mt-1 text-lg font-semibold tabular-nums">{value ?? 0}</p>
      )}
    </Tag>
  )
}

function ChecksTable({ checks }) {
  const rows = checks ?? []
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No checks in this section.</p>
    )
  }

  return (
    <div className="rounded-md border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
            <th className="px-3 py-2 font-medium">Check</th>
            <th className="px-3 py-2 font-medium">Result</th>
            <th className="px-3 py-2 font-medium">Detail</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((check, index) => (
            <tr
              key={`${check.area || check.check || 'check'}-${index}`}
              className={cn(
                'border-b border-border last:border-0 align-top',
                ['WARN', 'FAIL'].includes(normaliseCheckStatus(check.status)) && 'bg-amber-500/5',
              )}
            >
              <td className="px-3 py-2 font-medium">{check.area || check.check || '—'}</td>
              <td className="px-3 py-2 whitespace-nowrap">
                <Badge variant={checkStatusVariant(check.status)}>
                  {checkStatusLabel(check.status)}
                </Badge>
              </td>
              <td className="px-3 py-2 text-muted-foreground">{check.detail || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function AgentExchange({ loading, data }) {
  const [openSections, setOpenSections] = useState({
    summary: false,
    storage: false,
    logsChart: true,
    groups: false,
    checks: false,
  })
  const [statusFilter, setStatusFilter] = useState('all')

  const snapshot = exchangeHealthSnapshotFromRmmData(data)
  const payload = snapshot?.payload ?? null
  const checks = assessmentChecks(payload)
  const groupedChecks = useMemo(() => groupExchangeChecks(checks), [checks])
  const storageSections = useMemo(
    () => storageBreakdownFromExchangePayload(payload),
    [payload],
  )
  const transactionLogSeries = useMemo(
    () => transactionLogSeriesFromPayload(payload),
    [payload],
  )

  const filteredChecks = useMemo(() => {
    if (statusFilter === 'all') return checks
    return checks.filter(
      (check) => normaliseCheckStatus(check.status) === statusFilter,
    )
  }, [checks, statusFilter])

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (!snapshot) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{EXCHANGE_TAB_DESCRIPTION}</p>
        <Card className="border-dashed">
          <CardContent className="pt-6 text-sm text-muted-foreground">
            No Exchange health data for this device yet. Deploy the {EXCHANGE_HEALTH_SLUG} RMM
            script from Atera (run as System on the Exchange server) to populate this tab.
          </CardContent>
        </Card>
      </div>
    )
  }

  const collapsedHeader = [
    payload?.interpretation,
    payload?.script_version ? `Script v${payload.script_version}` : null,
    snapshot.collected_at ? `Collected ${formatDateTime(snapshot.collected_at)}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  function setSectionOpen(key, open) {
    setOpenSections((prev) => ({ ...prev, [key]: open }))
  }

  function selectStatusFilter(next) {
    setStatusFilter((current) => (current === next ? 'all' : next))
    setSectionOpen('checks', true)
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{EXCHANGE_TAB_DESCRIPTION}</p>

      <CollapsePanel
        id="exchange-health-overview"
        title="Exchange Server health"
        collapsedSummary={collapsedHeader}
        open={openSections.summary}
        onOpenChange={(open) => setSectionOpen('summary', open)}
        pill={payload?.overall_status ? toolkitStatusLabel(payload.overall_status) : undefined}
        pillVariant={statusPillVariant(payload?.overall_status)}
      >
        <p className="mb-4 text-xs text-muted-foreground">
          {payload?.script_version ? (
            <span>Script version {payload.script_version}</span>
          ) : (
            <span>Script version not reported</span>
          )}
          {snapshot.collected_at && (
            <span>{' · '}Collected {formatDateTime(snapshot.collected_at)}</span>
          )}
        </p>
        {payload?.interpretation && (
          <p className="mb-4 text-sm text-muted-foreground">{payload.interpretation}</p>
        )}
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <CountTile label="Checks run" value={payload?.checks_run ?? checks.length} />
          <CountTile
            label="Passed"
            value={payload?.passed}
            variant="success"
            active={statusFilter === 'PASS'}
            onClick={() => selectStatusFilter('PASS')}
          />
          <CountTile
            label="Warnings"
            value={payload?.warnings}
            variant="warning"
            active={statusFilter === 'WARN'}
            onClick={() => selectStatusFilter('WARN')}
          />
          <CountTile
            label="Failures"
            value={payload?.failures}
            variant="destructive"
            active={statusFilter === 'FAIL'}
            onClick={() => selectStatusFilter('FAIL')}
          />
        </div>
      </CollapsePanel>

      {storageSections.length > 0 && (
        <CollapsePanel
          id="exchange-health-storage"
          title="Storage breakdown"
          collapsedSummary={`${storageSections.length} Exchange path(s) · TreeSize-style folder list`}
          open={openSections.storage}
          onOpenChange={(open) => setSectionOpen('storage', open)}
        >
          <FolderUsageErrorBoundary fallbackHint="Re-run exchange-health-status on the server if this persists.">
            <div className="space-y-6">
              {storageSections.map((section, index) => (
                <div key={section.id || `${section.label}-${index}`} className="space-y-2">
                  <div>
                    <p className="text-sm font-medium">{section.label}</p>
                    {section.path && (
                      <p className="text-xs text-muted-foreground break-all" title={section.path}>
                        {section.path}
                        {section.total_gb != null && ` · ${section.total_gb} GB total`}
                      </p>
                    )}
                  </div>
                  <FolderUsageList
                    folders={section.folders}
                    percentKey="percent_of_total"
                    emptyMessage="No folder data for this path."
                  />
                </div>
              ))}
            </div>
          </FolderUsageErrorBoundary>
        </CollapsePanel>
      )}

      <CollapsePanel
        id="exchange-health-log-chart"
        title="Transaction logs (14 days)"
        collapsedSummary={
          transactionLogSeries.length > 0
            ? `${transactionLogSeries.length} database log folder${transactionLogSeries.length === 1 ? '' : 's'}`
            : 'Needs exchange-health-status v1.6+'
        }
        open={openSections.logsChart}
        onOpenChange={(open) => setSectionOpen('logsChart', open)}
      >
        {transactionLogSeries.length > 0 ? (
          <ExchangeTransactionLogChart series={transactionLogSeries} />
        ) : (
          <p className="text-sm text-muted-foreground">
            No 14-day log chart yet. Redeploy and run{' '}
            <span className="font-mono text-xs">exchange-health-status</span> v1.6 or newer on this
            Exchange server to collect log counts by day.
          </p>
        )}
      </CollapsePanel>

      <CollapsePanel
        id="exchange-health-groups"
        title="Health checks by area"
        collapsedSummary={`${checks.length} check(s) across ${groupedChecks.length} section(s)`}
        open={openSections.groups}
        onOpenChange={(open) => setSectionOpen('groups', open)}
      >
        <div className="space-y-6">
          {groupedChecks.map((group) => (
            <div key={group.title} className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {group.title}
              </p>
              <ChecksTable checks={group.items} />
            </div>
          ))}
        </div>
      </CollapsePanel>

      <CollapsePanel
        id="exchange-health-checks"
        title="All health checks"
        collapsedSummary={
          statusFilter === 'all'
            ? `${checks.length} check(s)`
            : `${filteredChecks.length} ${statusFilter} check(s) (filtered)`
        }
        open={openSections.checks}
        onOpenChange={(open) => setSectionOpen('checks', open)}
      >
        {statusFilter !== 'all' && (
          <p className="mb-3 text-xs text-muted-foreground">
            Filter: {checkStatusLabel(statusFilter)}.{' '}
            <button
              type="button"
              className="underline hover:text-foreground"
              onClick={() => setStatusFilter('all')}
            >
              Clear filter
            </button>
          </p>
        )}
        <ChecksTable checks={filteredChecks} />
      </CollapsePanel>
    </div>
  )
}
